/* eslint-disable @next/next/no-img-element */
import { Fragment, useContext, useState, useEffect } from "react";
import { Formik } from "formik";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { format } from "date-fns";
import classNames from "classnames";
import { createRideCredit } from "utils/ride-credit";
import AppContext from "context/app";

export default function ClaimCreditPanel({
  open,
  setOpen,
  attraction,
  mutate,
}) {
  const { user } = useContext(AppContext);
  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    setCurrentDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  }, [open]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-30 overflow-hidden"
        onClose={setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0" />

          <div className="fixed inset-y-0 right-0 flex max-w-full md:pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-2xl">
                <Formik
                  initialValues={{
                    rideDate: currentDate,
                  }}
                  onSubmit={(values, { setSubmitting }) => {
                    createRideCredit(
                      values,
                      setSubmitting,
                      attraction,
                      setOpen,
                      mutate,
                      user
                    );
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    isSubmitting,
                    /* and other goodies */
                  }) => (
                    <form
                      className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl"
                      onSubmit={handleSubmit}
                    >
                      <div className="flex-1">
                        {/* Header */}
                        <div className="bg-gray-50 px-4 py-6 sm:px-6">
                          <div className="flex items-start justify-between space-x-3">
                            <div className="space-y-1">
                              <Dialog.Title className="text-lg font-medium text-gray-900">
                                {attraction?.Type === "RESTROOM"
                                  ? "Claim Restroom Credit"
                                  : "Claim Ride Credit"}
                              </Dialog.Title>
                            </div>
                            <div className="flex h-7 items-center">
                              <button
                                type="button"
                                className="text-gray-400 hover:text-gray-500"
                                onClick={() => setOpen(false)}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Divider container */}
                        <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                          {/* Ride Date */}
                          <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                            <div>
                              <label
                                htmlFor="rideDate"
                                className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                              >
                                {attraction?.Type === "RESTROOM"
                                  ? "Date"
                                  : "Date Experienced"}
                              </label>
                            </div>
                            <div className="sm:col-span-2">
                              <input
                                type="datetime-local"
                                name="rideDate"
                                id="rideDate"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.rideDate}
                              />
                            </div>
                          </div>

                          {/* Ride Time */}
                          <div
                            className={classNames({
                              "space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5": true,
                              "!hidden": attraction?.Type === "RESTROOM",
                            })}
                          >
                            <div>
                              <label
                                htmlFor="rideTime"
                                className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                              >
                                Time of day
                              </label>
                            </div>
                            <div className="sm:col-span-2">
                              <select
                                name="rideTime"
                                id="rideTime"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="UNKNOWN">Unknown</option>
                                <option value="MORNING">Morning</option>
                                <option value="AFTERNOON">Afternoon</option>
                                <option value="NIGHT">Night</option>
                                <option value="EXCLUSIVE">
                                  Exclusive (ERT, Special Event, etc)
                                </option>
                              </select>
                            </div>
                          </div>

                          {/* Ride Position */}
                          <div className="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                            <div>
                              <label
                                htmlFor="ridePosition"
                                className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                              >
                                {attraction?.Type === "RESTROOM"
                                  ? "Number 1 or 2"
                                  : "Position"}
                              </label>
                            </div>
                            <div className="sm:col-span-2">
                              <select
                                name="ridePosition"
                                id="ridePosition"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="UNKNOWN">Unknown</option>
                                <option value="FRONT">
                                  {attraction?.Type === "RESTROOM"
                                    ? "Number 1"
                                    : "Front"}
                                </option>
                                <option value="MIDDLE">
                                  {attraction?.Type === "RESTROOM"
                                    ? "Both"
                                    : "Middle"}
                                </option>
                                <option value="BACK">
                                  {attraction?.Type === "RESTROOM"
                                    ? "Number 2"
                                    : "Back"}
                                </option>
                              </select>
                            </div>
                          </div>

                          {/* Ride Row */}
                          <div
                            className={classNames({
                              "space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5": true,
                              "!hidden": attraction?.Type === "RESTROOM",
                            })}
                          >
                            <div>
                              <label
                                htmlFor="rideRow"
                                className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                              >
                                Row
                              </label>
                            </div>
                            <div className="sm:col-span-2">
                              <input
                                type="text"
                                name="rideRow"
                                id="rideRow"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                          </div>

                          {/* Ride Side */}
                          <div
                            className={classNames({
                              "space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5": true,
                              "!hidden": attraction?.Type === "RESTROOM",
                            })}
                          >
                            <div>
                              <label
                                htmlFor="rideSide"
                                className="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"
                              >
                                Side
                              </label>
                            </div>
                            <div className="sm:col-span-2">
                              <select
                                name="rideSide"
                                id="rideSide"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="UNKNOWN">Unknown</option>
                                <option value="LEFT">Left</option>
                                <option value="MIDDLE">Middle</option>
                                <option value="RIGHT">Right</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            onClick={() => setOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed"
                            disabled={isSubmitting}
                          >
                            {!isSubmitting ? (
                              "Claim Credit"
                            ) : (
                              <>
                                <svg
                                  className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                Claiming...
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
